import { useState, useEffect } from "react";
import { Star, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Reveal } from "./Reveal";
import { TESTIMONIALS } from "../../data/site";
import { createReview, getReviews } from "../../lib/api";

function ReviewCard({ r, testid }) {
  return (
    <figure
      data-testid={testid}
      className="h-full rounded-3xl border border-black/5 bg-white card-soft p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1.5"
    >
      <div className="flex mb-4 text-brand-orange text-lg tracking-wide">{"★".repeat(r.rating)}</div>
      <blockquote className="text-brand-ink leading-relaxed flex-1">“{r.text}”</blockquote>
      <figcaption className="mt-6 flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-brand-blue/10 text-brand-blue font-display font-bold text-sm">
          {r.initials}
        </span>
        <span className="text-sm">
          <span className="block font-semibold text-brand-ink">{r.name}</span>
          <span className="text-brand-muted">{r.meta}</span>
        </span>
      </figcaption>
    </figure>
  );
}

const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((w) => w[0]).join("") || name.slice(0, 2)).toUpperCase();
};

export default function ReviewsSection() {
  const [dynamic, setDynamic] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 0, text: "", company: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getReviews()
      .then((data) =>
        setDynamic(
          (data || []).map((d) => ({
            initials: d.initials || initialsOf(d.name),
            name: d.name,
            meta: d.location,
            rating: d.rating,
            text: d.text,
          }))
        )
      )
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name");
    if (!form.rating) return toast.error("Please select a star rating");
    if (form.text.trim().length < 5) return toast.error("Please write a short review");
    setSubmitting(true);
    try {
      const r = await createReview({ name: form.name, rating: form.rating, text: form.text, company: form.company });
      setDynamic((prev) => [
        { initials: r.initials || initialsOf(r.name), name: r.name, meta: r.location, rating: r.rating, text: r.text },
        ...prev,
      ]);
      setForm({ name: "", rating: 0, text: "", company: "" });
      setHover(0);
      toast.success("Thank you! Your review has been posted.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const allReviews = [...dynamic, ...TESTIMONIALS];

  return (
    <section id="reviews" className="py-24 px-6 scroll-mt-24">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold text-center">Customer Reviews</p>
          <h2 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-brand-ink text-center">
            What Our Customers Say
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {allReviews.map((r, i) => (
            <Reveal key={`${r.name}-${i}`} delay={(i % 3) * 0.1}>
              <ReviewCard r={r} testid={`testimonial-${i}`} />
            </Reveal>
          ))}
        </div>

        {/* Share Your Experience */}
        <Reveal>
          <div className="mt-16 max-w-2xl mx-auto rounded-3xl border border-black/5 bg-white card-soft p-8 sm:p-10" data-testid="review-form-card">
            <div className="text-center">
              <p className="text-sm uppercase tracking-[0.25em] text-brand-blue font-semibold">Share Your Experience</p>
              <h3 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-brand-ink">
                Write a Review
              </h3>
            </div>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="review-form">
              <div>
                <Label htmlFor="rv-name">Full name</Label>
                <Input id="rv-name" data-testid="review-name" className="mt-1.5" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" />
              </div>
              <div>
                <Label>Your rating</Label>
                <div className="mt-2 flex items-center gap-1.5" data-testid="review-rating" role="radiogroup" aria-label="Star rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      aria-label={`${n} star`}
                      aria-checked={form.rating === n}
                      role="radio"
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          (hover || form.rating) >= n ? "fill-brand-orange text-brand-orange" : "text-black/20"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="rv-text">Your review</Label>
                <Textarea id="rv-text" data-testid="review-text" rows={4} className="mt-1.5" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="Tell us about your experience..." />
              </div>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="hidden" aria-hidden="true" />
              <button
                type="submit"
                data-testid="review-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold py-3.5 rounded-2xl hover:scale-[1.01] active:scale-95 transition-transform disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting...</> : <>Submit Review <Send className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
